import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	Assignment,
	AssignmentStatus,
	addPersonAssignment,
	getAssignments,
	PersonAssignmentCreateType,
	statusToThemeKey,
} from "../../api";
import { getAssignmentBoardConfigs } from "../../api/assignments/assignment-board-config";
import { getPersonAssignments } from "../../api/assignments/person-assignment";
import { getVotes } from "../../api/vote";
import { formatRelativeTimeShort } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";
import { AssignmentFilterBar, useAssignmentFilter } from "../shared/assignment-picker";

interface UpvoteInfo {
	assignmentId: number;
	username: string;
	created: string;
}

interface ExistingPlacement {
	column: string;
	statusStr: string;
}

interface AddToRoleDialogProps {
	personId: number;
	open: boolean;
	onClose: () => void;
	onAdded?: () => void;
}

export const AddToRoleDialog = ({
	personId,
	open,
	onClose,
	onAdded,
}: AddToRoleDialogProps) => {
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [upvotes, setUpvotes] = useState<UpvoteInfo[]>([]);
	const [placements, setPlacements] = useState<Map<number, ExistingPlacement>>(
		new Map()
	);
	const [isLoading, setIsLoading] = useState(false);
	const {
		searchQuery,
		setSearchQuery,
		activeStatusKeys,
		setActiveStatusKeys,
		filteredAssignments: eligibleAssignments,
	} = useAssignmentFilter(assignments);
	const { currentUser } = useGlobalState();
	const theme = useTheme();
	const normalizedQuery = searchQuery.trim().toLowerCase();

	useEffect(() => {
		if (!open) return;
		setIsLoading(true);
		const assignmentsPromise =
			assignments.length === 0
				? getAssignments(undefined, { creatorInMyUpGroup: true })
				: Promise.resolve(assignments);
		Promise.all([
			assignmentsPromise,
			getVotes({
				person: personId,
				grade: "up",
				ordering: "-created",
			}),
			getPersonAssignments({ personIds: [personId] }),
		])
			.then(([assignmentsRes, votesRes, paRes]) => {
				if (assignments.length === 0) setAssignments(assignmentsRes);
				const seen = new Set<number>();
				const ordered: UpvoteInfo[] = [];
				for (const vote of votesRes.results) {
					if (!seen.has(vote.assignment)) {
						seen.add(vote.assignment);
						ordered.push({
							assignmentId: vote.assignment,
							username: vote.user_username ?? "",
							created: vote.created,
						});
					}
				}
				setUpvotes(ordered);
				const placementMap = new Map<number, ExistingPlacement>();
				for (const pa of paRes) {
					if (
						pa.assignment !== undefined &&
						!placementMap.has(pa.assignment)
					) {
						placementMap.set(pa.assignment, {
							column: pa.column,
							statusStr: pa.status_str ?? "",
						});
					}
				}
				setPlacements(placementMap);
			})
			.finally(() => setIsLoading(false));
	}, [open, personId]);

	const eligibleById = new Map(eligibleAssignments.map((a) => [a.id, a]));
	const upvotedRows = upvotes
		.map((u) => {
			const assignment = eligibleById.get(u.assignmentId);
			return assignment ? { assignment, upvote: u } : null;
		})
		.filter(
			(row): row is { assignment: Assignment; upvote: UpvoteInfo } => row !== null
		);
	const upvotedIdSet = new Set(upvotedRows.map((row) => row.assignment.id));

	const otherAssignments = eligibleAssignments.filter((a) => !upvotedIdSet.has(a.id));

	const addPersonToAssignment = async (assignmentId: number) => {
		const assignmentConfig = await getAssignmentBoardConfigs(assignmentId).then(
			(res) => res.results[0]
		);

		const column = assignmentConfig?.config.columns[0].name;
		const statusStr = assignmentConfig?.config.columns[0].statuses[0].name;

		const data: PersonAssignmentCreateType = {
			column,
			status_str: statusStr,
			assignment: assignmentId,
			person: personId,
			created_by: currentUser.id,
			assignee: currentUser.id,
		};

		await addPersonAssignment({ data });
		setPlacements((prev) => {
			const next = new Map(prev);
			next.set(assignmentId, { column, statusStr: statusStr ?? "" });
			return next;
		});
		onAdded?.();
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			data-component="add-to-role-dialog"
		>
			<DialogTitle>Add to Board</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<AssignmentFilterBar
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					activeStatusKeys={activeStatusKeys}
					onStatusKeysChange={setActiveStatusKeys}
					autoFocus
				/>
				{isLoading ? (
					<Stack
						direction="row"
						spacing={2}
						alignItems="center"
						justifyContent="center"
						sx={{ py: 3 }}
						data-component="loading-assignments"
					>
						<CircularProgress size={20} />
						<Typography>Loading roles...</Typography>
					</Stack>
				) : eligibleAssignments.length === 0 ? (
					<Typography
						sx={{ p: 2, textAlign: "center" }}
						color="text.secondary"
					>
						{normalizedQuery
							? "No roles match your search"
							: "No active roles available"}
					</Typography>
				) : (
					<List data-component="assignments-list">
						{[
							...upvotedRows.map((row) => ({
								assignment: row.assignment,
								upvote: row.upvote,
								keyPrefix: "upvoted",
								dataComponent: "assignment-list-item-upvoted",
							})),
							...otherAssignments.map((assignment) => ({
								assignment,
								upvote: null,
								keyPrefix: "other",
								dataComponent: "assignment-list-item",
							})),
						].map(({ assignment: a, upvote, keyPrefix, dataComponent }) => {
							const placement = placements.get(a.id);
							return (
								<ListItemButton
									key={`${keyPrefix}-${a.id}`}
									onClick={
										placement
											? undefined
											: () => addPersonToAssignment(a.id)
									}
									data-component={dataComponent}
									sx={
										placement
											? {
													pointerEvents: "none",
													"&:hover": {
														backgroundColor: "transparent",
													},
												}
											: undefined
									}
								>
									<ListItemText
										primary={a.name}
										slotProps={{ secondary: { component: "div" } }}
										secondary={
											<Stack
												direction="row"
												spacing={0.5}
												alignItems="center"
												flexWrap="wrap"
												useFlexGap
											>
												{a.company_name && (
													<span>@{a.company_name}</span>
												)}
												{upvote && (
													<Chip
														size="small"
														variant="filled"
														color="primary"
														label={
															upvote.username
																? `Upvoted by ${upvote.username} · ${formatRelativeTimeShort(upvote.created)}`
																: `Upvoted · ${formatRelativeTimeShort(upvote.created)}`
														}
														data-component="upvoted-chip"
														sx={{
															height: 18,
															fontSize: "0.7rem",
														}}
													/>
												)}
												{placement && (
													<Chip
														size="small"
														variant="filled"
														color="secondary"
														label={`Already on this board: ${placement.column}${placement.statusStr ? ` · ${placement.statusStr}` : ""}`}
														data-component="already-on-board-chip"
														sx={{
															height: 18,
															fontSize: "0.7rem",
														}}
													/>
												)}
											</Stack>
										}
									/>
									{placement && (
										<Tooltip title="Open board">
											<IconButton
												component="a"
												href={`/board/v2?assignmentId=${a.id}`}
												target="_blank"
												rel="noopener"
												size="small"
												data-component="open-board-button"
												onClick={(e) => e.stopPropagation()}
												sx={{ pointerEvents: "auto", ml: 1 }}
											>
												<OpenInNewIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
									<Chip
										size="small"
										label={a.status_display}
										sx={{
											backgroundColor:
												theme.assignmentStatus[
													statusToThemeKey[
														AssignmentStatus[a.status]
													]
												],
											color: "common.black",
											ml: 1,
										}}
									/>
								</ListItemButton>
							);
						})}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
			</DialogActions>
		</Dialog>
	);
};
